import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  Button,
  Chip,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { Autocomplete, ToggleButton, ToggleButtonGroup, createFilterOptions } from "@material-ui/lab";
import {
  CloudUpload as CloudUploadIcon,
  CreateOutlined as CreateOutlinedIcon,
  DeleteOutline,
  GetApp as GetAppIcon,
  MenuBook as MenuBookIcon,
  Search as SearchIcon,
} from "@material-ui/icons";
import swal from "sweetalert";
import { toast } from "react-hot-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import TablaSeleccionable from "../../../../components/shared/tabla-seleccionable";
import ImportadorMasivo from "../../../../components/shared/importador-masivo";
import SelectorLibros from "./selector-libros";
import {
  agregarLibrosACurso,
  fetchCursosPlanLector,
  fetchLibrosCurso,
  fetchLibrosSede,
  quitarLibrosCurso,
} from "../../../../shared/fetchers/plan-lector/librosCurso";
import { exportarExcel } from "../../../../shared/utils/exportar-excel";

const STORAGE_KEY = "planLector.agregarLibrosCurso";

const estadoVacio = {
  cursoId: null,
  instituto: null,
  recientes: [],
  modos: {},
  selecciones: {},
};

const leerEstado = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return estadoVacio;
    const data = JSON.parse(raw);
    return {
      ...estadoVacio,
      ...data,
      recientes: Array.isArray(data.recientes) ? data.recientes.map(String) : [],
      modos: data.modos && typeof data.modos === "object" ? data.modos : {},
      selecciones: data.selecciones && typeof data.selecciones === "object" ? data.selecciones : {},
    };
  } catch {
    return estadoVacio;
  }
};

const filtrarCursosBase = createFilterOptions({
  stringify: (curso) => `${curso.idCursoPlanLector} ${curso.instituto ?? ""} ${curso.curso ?? ""}`,
  limit: 50,
});

const claveLibro = (libro) => String(libro.idLibro);
const textoBusquedaLibro = (libro) =>
  `${libro.idLibro} ${libro.title ?? ""} ${libro.author ?? ""} ${libro.publisher ?? ""}`;

const columnasLibros = [
  { field: "idLibro", label: "ID", width: 120, render: (libro) => <code>{libro.idLibro}</code> },
  { field: "title", label: "Título" },
  { field: "author", label: "Autor" },
  { field: "publisher", label: "Editorial" },
];

const etiquetaCurso = (curso) => (curso ? `${curso.curso} · ${curso.instituto}` : "");

const AgregarEliminarLibrosCursoPlanLector = () => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const [estado, setEstado] = useState(leerEstado);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(estado));
    } catch {
      // La sesión puede estar llena o no disponible.
    }
  }, [estado]);

  const cursosQuery = useQuery({
    queryKey: ["cursosPlanLector"],
    queryFn: fetchCursosPlanLector,
    staleTime: 5 * 60 * 1000,
  });
  const cursos = useMemo(() => cursosQuery.data ?? [], [cursosQuery.data]);

  const curso = useMemo(
    () => cursos.find((item) => String(item.idCursoPlanLector) === String(estado.cursoId)) ?? null,
    [cursos, estado.cursoId]
  );
  const cursoId = curso ? String(curso.idCursoPlanLector) : null;

  const institutos = useMemo(() => {
    const nombres = new Set(cursos.map((item) => item.instituto).filter(Boolean));
    return [...nombres].sort((a, b) => a.localeCompare(b, "es"));
  }, [cursos]);

  const institutoActivo =
    cursosQuery.isSuccess && institutos.includes(estado.instituto) ? estado.instituto : null;

  useEffect(() => {
    if (!cursosQuery.isSuccess || !estado.cursoId) return;
    const existe = cursos.some((item) => String(item.idCursoPlanLector) === String(estado.cursoId));
    if (!existe) setEstado((prev) => ({ ...prev, cursoId: null }));
  }, [cursosQuery.isSuccess, cursos, estado.cursoId]);

  const librosQuery = useQuery({
    queryKey: ["librosCurso", cursoId],
    queryFn: () => fetchLibrosCurso(cursoId),
    enabled: Boolean(cursoId),
    staleTime: 30 * 1000,
  });
  const librosCurso = useMemo(() => librosQuery.data ?? [], [librosQuery.data]);

  const catalogoQuery = useQuery({
    queryKey: ["librosSede", curso?.instituto],
    queryFn: () => fetchLibrosSede(curso.instituto),
    enabled: Boolean(curso?.instituto),
    staleTime: 5 * 60 * 1000,
  });
  const catalogo = useMemo(() => catalogoQuery.data ?? [], [catalogoQuery.data]);

  const catalogoListo = catalogoQuery.isSuccess && librosQuery.isSuccess;
  const errorCarga = librosQuery.isError
    ? librosQuery.error.message
    : catalogoQuery.isError
    ? catalogoQuery.error.message
    : null;
  const motivoBloqueo = errorCarga
    ? errorCarga
    : !librosQuery.isSuccess
    ? "Espera a que carguen los libros del curso."
    : !catalogoQuery.isSuccess
    ? "Espera a que carguen los libros disponibles del instituto."
    : catalogo.length === 0
    ? "Este instituto no tiene libros disponibles para agregar."
    : null;

  useEffect(() => {
    if (!cursoId || !librosQuery.isSuccess) return;
    const enCurso = new Set(librosCurso.map((libro) => String(libro.idLibro)));
    setEstado((prev) => {
      const actual = prev.selecciones?.[cursoId];
      if (!actual?.length) return prev;
      const siguiente = actual.filter((libro) => !enCurso.has(String(libro.idLibro)));
      if (siguiente.length === actual.length) return prev;
      return { ...prev, selecciones: { ...prev.selecciones, [cursoId]: siguiente } };
    });
  }, [cursoId, librosQuery.isSuccess, librosCurso]);

  const opcionesCurso = useMemo(() => {
    const lista = institutoActivo
      ? cursos.filter((item) => item.instituto === institutoActivo)
      : cursos;
    if (curso && !lista.some((item) => String(item.idCursoPlanLector) === cursoId)) {
      return [curso, ...lista];
    }
    return lista;
  }, [cursos, institutoActivo, curso, cursoId]);

  const filtrarCursos = useCallback(
    (options, params) => {
      if (!institutoActivo && !params.inputValue.trim()) return [];
      return filtrarCursosBase(options, params);
    },
    [institutoActivo]
  );

  const recientes = useMemo(() => {
    const porId = new Map(cursos.map((item) => [String(item.idCursoPlanLector), item]));
    return (estado.recientes || []).map((id) => porId.get(String(id))).filter(Boolean);
  }, [cursos, estado.recientes]);

  const idsEnCurso = useMemo(
    () => new Set(librosCurso.map((libro) => String(libro.idLibro))),
    [librosCurso]
  );
  const deshabilitados = useMemo(() => {
    const map = new Map();
    librosCurso.forEach((libro) => map.set(String(libro.idLibro), "Ya está en el curso"));
    return map;
  }, [librosCurso]);

  const catalogoPorId = useMemo(() => {
    const map = new Map();
    catalogo.forEach((libro) => map.set(String(libro.idLibro).trim(), libro));
    return map;
  }, [catalogo]);

  const modoAgregar = (cursoId && estado.modos?.[cursoId]) || "buscar";
  const seleccion = (cursoId && estado.selecciones?.[cursoId]) || [];

  const elegirCurso = (nuevo) => {
    setEstado((prev) => {
      if (!nuevo) return { ...prev, cursoId: null };
      const id = String(nuevo.idCursoPlanLector);
      return {
        ...prev,
        cursoId: id,
        recientes: [id, ...(prev.recientes || []).filter((item) => item !== id)].slice(0, 8),
      };
    });
  };

  const setModoAgregar = (valor) => {
    if (!cursoId) return;
    setEstado((prev) => ({ ...prev, modos: { ...prev.modos, [cursoId]: valor } }));
  };

  const setSeleccion = (lista) => {
    if (!cursoId) return;
    setEstado((prev) => ({
      ...prev,
      selecciones: {
        ...prev.selecciones,
        [cursoId]: lista.map((libro) => ({
          idLibro: libro.idLibro,
          title: libro.title ?? "",
          author: libro.author ?? "",
          publisher: libro.publisher ?? "",
        })),
      },
    }));
  };

  const recargarLibros = async (id) => {
    await queryClient.refetchQueries({ queryKey: ["librosCurso", String(id)] });
  };

  const agregarSeleccion = async (libros) => {
    const id = curso.idCursoPlanLector;
    const nuevos = libros.filter((libro) => !idsEnCurso.has(String(libro.idLibro)));
    if (!nuevos.length) throw new Error("Esos libros ya están en el curso");

    setGuardando(true);
    try {
      const info = await agregarLibrosACurso({
        idCurso: id,
        idLibros: nuevos.map((libro) => libro.idLibro),
      });
      await recargarLibros(id);
      setSeleccion([]);
      toast.success(info);
    } finally {
      setGuardando(false);
    }
  };

  const agregarMasivo = async (filas) => {
    const id = curso.idCursoPlanLector;
    setGuardando(true);
    try {
      const info = await agregarLibrosACurso({
        idCurso: id,
        idLibros: filas.map((fila) => fila.item.idLibro),
      });
      await recargarLibros(id);
      return info;
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async (seleccionados, limpiarSeleccion) => {
    const todos = seleccionados.length === librosCurso.length;
    const confirmar = await swal({
      title: `¿Quitar ${seleccionados.length} libro${seleccionados.length === 1 ? "" : "s"} del curso?`,
      text: todos
        ? `«${curso.curso}» quedará sin libros.`
        : `Se quitarán de «${curso.curso}». El resto del curso no cambia.`,
      icon: "warning",
      buttons: ["Cancelar", "Quitar"],
      dangerMode: true,
    });
    if (!confirmar) return;

    const quitarIds = new Set(seleccionados.map((libro) => String(libro.idLibro)));
    const restantes = librosCurso
      .map((libro) => String(libro.idLibro))
      .filter((id) => !quitarIds.has(id));

    setGuardando(true);
    try {
      await quitarLibrosCurso({ idCurso: curso.idCursoPlanLector, idLibrosRestantes: restantes });
      await recargarLibros(curso.idCursoPlanLector);
      const actualizados = queryClient.getQueryData(["librosCurso", cursoId]) ?? [];
      const siguen = actualizados.filter((libro) => quitarIds.has(String(libro.idLibro)));
      if (siguen.length) {
        throw new Error("Algunos libros siguen en el curso. Revisa la lista e inténtalo de nuevo.");
      }
      limpiarSeleccion();
      toast.success(
        `Se quitaron ${seleccionados.length} libro${seleccionados.length === 1 ? "" : "s"} del curso`
      );
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGuardando(false);
    }
  };

  const resolverLibro = useCallback((id) => catalogoPorId.get(String(id).trim()) ?? null, [catalogoPorId]);
  const estadoExistente = useCallback(
    (_, libro) =>
      idsEnCurso.has(String(libro.idLibro))
        ? { status: "existente", mensaje: "Ya está en el curso" }
        : null,
    [idsEnCurso]
  );

  return (
    <>
      <div className="mb-4 d-flex align-items-center">
        <div className="admin-page-tilte">(</div>
        <CreateOutlinedIcon />
        <div className="admin-page-tilte mr-2">)</div>
        <div className="admin-page-tilte">Agregar o eliminar libros a un curso lector</div>
      </div>
      <Container fluid>
        <Row>
          <Col xs={12} className="content-white-box px-4 py-4">
            <div className="d-flex align-items-center">
              <MenuBookIcon style={{ fontSize: 20 }} />
              <div className="admin-main-title-dark-gray ml-2 mt-1">Curso</div>
            </div>
            <div className="admin-muted-text-small mt-1 mb-3">
              Busca por instituto, nombre o ID. Si cambias de curso se conserva lo que tengas
              seleccionado o pegado, también después de subir.
            </div>

            <label className="admin-label-text d-block">Instituto</label>
            <Autocomplete
              options={institutos}
              value={institutoActivo}
              onChange={(_, value) => setEstado((prev) => ({ ...prev, instituto: value }))}
              disabled={cursosQuery.isLoading || guardando}
              noOptionsText="Sin resultados"
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="Filtra por instituto para ver sus cursos"
                />
              )}
            />

            <label className="admin-label-text mt-3 d-block">Curso</label>
            <Autocomplete
              options={opcionesCurso}
              filterOptions={filtrarCursos}
              value={curso}
              onChange={(_, value) => elegirCurso(value)}
              getOptionLabel={etiquetaCurso}
              getOptionSelected={(a, b) => String(a.idCursoPlanLector) === String(b.idCursoPlanLector)}
              loading={cursosQuery.isLoading}
              loadingText="Cargando cursos…"
              disabled={guardando}
              selectOnFocus
              noOptionsText={
                institutoActivo
                  ? "Este instituto no tiene cursos con ese texto"
                  : "Escribe el instituto, el nombre del curso o el ID"
              }
              renderOption={(option) => (
                <div>
                  <div>{option.curso}</div>
                  <div className="admin-muted-text-small">
                    {option.instituto} · ID {option.idCursoPlanLector}
                    {option.estudiantes != null ? ` · ${option.estudiantes} estudiantes` : ""}
                  </div>
                </div>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="Buscar por instituto, nombre o ID"
                />
              )}
            />
            {cursosQuery.isError && (
              <div className="d-flex align-items-center mt-2" style={{ gap: 8 }}>
                <span className="admin-muted-text-small" style={{ color: "#a8261e" }}>
                  {cursosQuery.error.message}
                </span>
                <Button size="small" onClick={() => cursosQuery.refetch()}>
                  Reintentar
                </Button>
              </div>
            )}

            {recientes.length > 0 && (
              <div className="d-flex flex-wrap align-items-center mt-3" style={{ gap: 8 }}>
                <span className="admin-muted-text-small">Recientes:</span>
                {recientes.map((item) => {
                  const activo = String(item.idCursoPlanLector) === cursoId;
                  return (
                    <Chip
                      key={item.idCursoPlanLector}
                      size="small"
                      label={etiquetaCurso(item)}
                      clickable
                      disabled={guardando}
                      onClick={() => elegirCurso(item)}
                      style={activo ? { background: "#5fbeaa", color: "#fff" } : undefined}
                      variant={activo ? "default" : "outlined"}
                    />
                  );
                })}
              </div>
            )}
          </Col>

          {curso && (
            <Col xs={12} className="content-white-box px-4 py-4 mt-3">
              <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                <MenuBookIcon style={{ fontSize: 22 }} />
                <div className="admin-main-title-dark-gray mt-1">
                  Agregar libros a <span style={{ color: "#5fbeaa" }}>{curso.curso}</span>
                </div>
                <Chip
                  size="small"
                  label={
                    librosQuery.isSuccess
                      ? `${librosCurso.length} libro${librosCurso.length === 1 ? "" : "s"} en el curso`
                      : "Cargando libros…"
                  }
                />
              </div>
              <div className="admin-muted-text-small mt-1">
                {curso.instituto} · ID {curso.idCursoPlanLector}
              </div>

              <ToggleButtonGroup
                size="small"
                exclusive
                value={modoAgregar}
                onChange={(_, value) => value && setModoAgregar(value)}
                className="mt-3 mb-3"
              >
                <ToggleButton value="buscar" className={classes.toggle}>
                  <SearchIcon fontSize="small" className="mr-1" /> Buscar y seleccionar
                </ToggleButton>
                <ToggleButton value="masiva" className={classes.toggle}>
                  <CloudUploadIcon fontSize="small" className="mr-1" /> Carga masiva
                </ToggleButton>
              </ToggleButtonGroup>

              {modoAgregar === "buscar" ? (
                <SelectorLibros
                  catalogo={catalogo}
                  deshabilitados={deshabilitados}
                  value={seleccion}
                  onChange={setSeleccion}
                  onAgregar={agregarSeleccion}
                  disabled={!catalogoListo || catalogo.length === 0 || guardando}
                />
              ) : (
                <ImportadorMasivo
                  persistKey={`libros-curso-${cursoId}`}
                  title="Carga masiva de libros"
                  description={
                    <>
                      Los libros se agregan a <strong>{curso.curso}</strong>. Pega la columna de IDs
                      (ej. <code>idLibro</code>) o sube un archivo. Antes de confirmar ves cuáles son
                      nuevos, cuáles ya están en el curso y cuáles no están disponibles para el instituto.
                    </>
                  }
                  idHeader="idLibro"
                  idLabel="ID libro"
                  templateExamples={catalogo.slice(0, 3).map((libro) => libro.idLibro)}
                  templateFileName={`plantilla-libros-${cursoId}`}
                  normalizeId={(id) => id.trim()}
                  resolveItem={resolverLibro}
                  itemLabel={(libro) => [libro.title, libro.author].filter(Boolean).join(" — ")}
                  getExistingState={estadoExistente}
                  existingLabel="Ya en el curso"
                  missingMessage="No está disponible para este instituto"
                  confirmLabel={(n) => `Agregar ${n} al curso`}
                  onConfirm={agregarMasivo}
                  disabled={!catalogoListo || catalogo.length === 0 || guardando}
                  disabledReason={motivoBloqueo}
                />
              )}
              {modoAgregar === "buscar" && motivoBloqueo && (
                <div className="d-flex align-items-center flex-wrap mt-2" style={{ gap: 8 }}>
                  <span
                    className="admin-muted-text-small"
                    style={{ color: errorCarga || catalogoListo ? "#a8261e" : undefined }}
                  >
                    {motivoBloqueo}
                  </span>
                  {errorCarga && (
                    <Button
                      size="small"
                      onClick={() => {
                        librosQuery.refetch();
                        catalogoQuery.refetch();
                      }}
                    >
                      Reintentar
                    </Button>
                  )}
                </div>
              )}
            </Col>
          )}

          {curso && (
            <Col xs={12} className="content-white-box px-4 py-4 mt-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap mb-2" style={{ gap: 12 }}>
                <div>
                  <div className="admin-main-title-dark-gray">Libros del curso</div>
                  <div className="admin-muted-text-small">
                    Busca por ID, título, autor o editorial y selecciona uno o varios para quitarlos.
                  </div>
                </div>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  disabled={!librosCurso.length}
                  onClick={() =>
                    exportarExcel({
                      rows: librosCurso,
                      columns: columnasLibros,
                      fileName: `libros-curso-${cursoId}`,
                      sheetName: String(curso.curso).replace(/[\\/?*[\]:]/g, " ").slice(0, 31) || "Libros",
                    })
                  }
                >
                  Exportar Excel
                </Button>
              </div>
              <TablaSeleccionable
                rows={librosCurso}
                rowKey={claveLibro}
                columns={columnasLibros}
                searchText={textoBusquedaLibro}
                searchPlaceholder="Buscar en el curso por ID, título, autor o editorial"
                loading={librosQuery.isLoading}
                selectable
                emptyMessage={
                  librosQuery.isError
                    ? "No se pudieron cargar los libros del curso"
                    : "Este curso no tiene libros"
                }
                initialRowsPerPage={25}
                renderBulkActions={(seleccionados, limpiar) => (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<DeleteOutline />}
                    disabled={guardando}
                    onClick={() => quitar(seleccionados, limpiar)}
                    classes={{ root: classes.quitarBtn }}
                  >
                    Quitar del curso
                  </Button>
                )}
              />
            </Col>
          )}
        </Row>
      </Container>
    </>
  );
};

export default AgregarEliminarLibrosCursoPlanLector;

const useStyles = makeStyles({
  toggle: {
    textTransform: "none",
    padding: "4px 12px",
    "&.Mui-selected": { background: "#5fbeaa", color: "#fff" },
    "&.Mui-selected:hover": { background: "#4ea493" },
  },
  quitarBtn: {
    background: "#dc3545",
    color: "#fff",
    "&:hover": { backgroundColor: "#c82333" },
  },
});
